import { useState, useCallback, type ChangeEvent } from "react";
import { Drawer, TextInput, Button, Row, Select } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import ApiHttp from "byzantine/src/ApiHttp";
import { useFetchRecipients } from "byzantine/src/dbbl/businessLogic/entities/recipients/requests";

type UnsavedRecipientDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  recipient: API.Recipient | null;
  onUpdate?: (updatedRecipient: API.Recipient) => void;
};

type FormState = {
  name: string;
  accountNumber: string;
  routingNumber: string;
  accountType: "checking" | "savings";
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const validate = (values: FormState): FormErrors => {
  const errors: FormErrors = {};
  if (!values.name.trim()) {
    errors.name = "Name is required";
  }
  if (!values.accountNumber.trim()) {
    errors.accountNumber = "Account number is required";
  }
  if (!values.routingNumber.trim()) {
    errors.routingNumber = "Routing number is required";
  } else if (!/^\d{9}$/.test(values.routingNumber.trim())) {
    errors.routingNumber = "Routing number must be exactly 9 digits";
  }
  if (!values.accountType) {
    errors.accountType = "Account type is required";
  }
  return errors;
};

const UnsavedRecipientDrawer = ({
  isOpen,
  onClose,
  recipient,
  onUpdate,
}: UnsavedRecipientDrawerProps) => {
  const { sendNotification } = useNotificationContext();
  const { send: refetchRecipients } = useFetchRecipients();

  const [form, setForm] = useState<FormState>({
    name: "",
    accountNumber: "",
    routingNumber: "",
    accountType: "checking",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  // Reset form when drawer opens with a new recipient or re-opens the same one
  const [lastRecipientId, setLastRecipientId] =
    useState<API.RecipientId | null>(null);
  if (recipient && recipient.id !== lastRecipientId) {
    setLastRecipientId(recipient.id);
    setForm({
      name: recipient.name || "",
      accountNumber: recipient.ach_destination?.account_number || "",
      routingNumber: recipient.ach_destination?.routing_number || "",
      accountType:
        (recipient.ach_destination?.account_type as "checking" | "savings") ||
        "checking",
    });
    setErrors({});
  }
  // Clear tracked id when drawer closes so re-opening the same recipient
  // properly resets the form instead of showing stale unsaved edits.
  if (!recipient && lastRecipientId !== null) {
    setLastRecipientId(null);
  }

  const handleChange = useCallback(
    (field: keyof FormState) =>
      (e: ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      },
    []
  );

  const handleSubmit = useCallback(async () => {
    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!recipient) return;

    setSubmitting(true);
    try {
      const response = await ApiHttp.fetch(
        "recipients/bulk_update/",
        { method: "POST" },
        {
          rows: [
            {
              uuid: recipient.id,
              name: form.name.trim(),
              type: recipient.type || "business",
              account_number: form.accountNumber.trim(),
              routing_number: form.routingNumber.trim(),
              account_type: form.accountType,
            },
          ],
        }
      );
      onUpdate?.((response as API.Recipient[])[0]);

      // Refresh recipients in Redux store
      refetchRecipients();

      sendNotification({
        type: "success",
        text: "Payment details updated.",
      });
      onClose();
    } catch {
      sendNotification({
        type: "negative",
        text: "Failed to update payment details.",
      });
    } finally {
      setSubmitting(false);
    }
  }, [form, recipient, refetchRecipients, sendNotification, onClose, onUpdate]);

  return (
    <Drawer
      depth="524px"
      isOpen={isOpen}
      onUserDismiss={onClose}
      showControls={false}
      paddingSize="xl"
    >
      <div className="margin--bottom--l">
        <h3 className="fontSize--l fontWeight--bold nds-sans">
          Edit unsaved recipient
        </h3>
        <p className="fontSize--s fontColor--secondary margin--top--xs">
          Changes apply to this payment only.
        </p>
      </div>

      <div className="margin--bottom--s">
        <h4 className="fontSize--s fontWeight--bold margin--bottom--s">ACH</h4>
      </div>

      <div className="margin--bottom--m">
        <TextInput
          label="Name"
          value={form.name}
          onChange={handleChange("name")}
          error={errors.name}
        />
      </div>

      <div className="margin--bottom--m">
        <TextInput
          label="Account number"
          value={form.accountNumber}
          onChange={handleChange("accountNumber")}
          error={errors.accountNumber}
        />
      </div>

      <div className="margin--bottom--m">
        <TextInput
          label="Routing number"
          value={form.routingNumber}
          onChange={handleChange("routingNumber")}
          error={errors.routingNumber}
        />
      </div>

      <div className="margin--bottom--l">
        <Select
          label="Account type"
          value={form.accountType}
          onChange={(value: "checking" | "savings") => {
            setForm((prev) => ({ ...prev, accountType: value }));
            setErrors((prev) => ({ ...prev, accountType: undefined }));
          }}
          errorText={errors.accountType}
        >
          <Select.Item value="checking">Checking</Select.Item>
          <Select.Item value="savings">Savings</Select.Item>
        </Select>
      </div>

      <Row justifyContent="end">
        <Button onClick={handleSubmit} disabled={submitting}>
          Update
        </Button>
      </Row>
    </Drawer>
  );
};

export default UnsavedRecipientDrawer;
